<script setup lang="ts">
import { reactive, onMounted, computed, defineAsyncComponent } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useRouter } from 'vue-router'
import { useForm11 } from '@/composables/useForm11'
import MainLayout from '@/components/layout/MainLayout.vue'
import type { Component } from 'vue'
import { IncomeType, useIncomeForm } from '@/composables/useIncomeForm'

const incomeForms: Partial<
  Record<
    IncomeType,
    {
      label: string
      component: Component
    }
  >
> = {
  [IncomeType.CanadianDividends]: {
    label: 'Canadian dividends',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/CanadianDividendsForm.vue'),
    ),
  },
  [IncomeType.DividendsDistributions]: {
    label: 'Dividends & distributions',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/DividendsDistributionsForm.vue'),
    ),
  },
  [IncomeType.EmpOffPen]: {
    label: 'Emp / Off / Pen not subject to PAYE',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/EmpOffPenForm.vue'),
    ),
  },
  [IncomeType.FeesCommissions]: {
    label: 'Fees and commissions',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/FeesCommissionsForm.vue'),
    ),
  },
  [IncomeType.ForeignPensions]: {
    label: 'Foreign pensions',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/ForeignPensionsForm.vue'),
    ),
  },
  [IncomeType.ForeignSalary]: {
    label: 'Foreign salary',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/ForeignSalaryForm.vue'),
    ),
  },
  [IncomeType.MaintenancePayments]: {
    label: 'Maintenance payments received',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/MaintenancePaymentsForm.vue'),
    ),
  },
  [IncomeType.RentalIncome]: {
    label: 'Rental income',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/RentalIncomeForm.vue'),
    ),
  },
  [IncomeType.TradingProfit]: {
    label: 'Trading profit or professional income',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/TradingProfitForm.vue'),
    ),
  },
  [IncomeType.UkDividends]: {
    label: 'UK dividends',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/UkDividendsForm.vue'),
    ),
  },
  [IncomeType.UsDividends]: {
    label: 'US dividends',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/UsDividendsForm.vue'),
    ),
  },
  [IncomeType.UntaxedStateIncome]: {
    label: 'Untaxed income arising in the State',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/UntaxedStateIncomeForm.vue'),
    ),
  },
  [IncomeType.IrishTaxedIncome]: {
    label: 'Irish taxed income',
    component: defineAsyncComponent(
      () => import('@/components/income-forms/IrishTaxedIncomeForm.vue'),
    ),
  },
}

interface IncomeField {
  amount: string
  [key: string]: string // Allow any string fields
}

interface IncomeData {
  [key: string]: IncomeField
}

const router = useRouter()
const form11 = useForm11()
const incomeForm = useIncomeForm()
const formData = reactive<IncomeData>({})

const selectedIncomeTypes = computed(
  () => (form11.selectedIncomeTypes.value || []) as IncomeType[],
)

onMounted(() => {
  if (selectedIncomeTypes.value.length === 0) {
    router.push('/form11/income-selection')
    return
  }

  // Initialize form data with empty amount field
  selectedIncomeTypes.value.forEach((type) => {
    formData[type] = { amount: '' }
  })
})

async function submitForm() {
  await incomeForm.saveForms()
  await form11.completeForm()
}
</script>

<template>
  <MainLayout>
    <div class="max-w-3xl mx-auto p-6">
      <h1 class="text-2xl font-bold mb-6">Income Declaration</h1>

      <p class="mb-6 text-gray-600">
        Please provide details for each income source you selected.
      </p>

      <div v-for="type in selectedIncomeTypes" :key="type" class="mb-8">
        <h2 class="text-xl font-semibold mb-4 pb-2 border-b">
          {{ incomeForms[type]?.label }}
        </h2>

        <component
          :is="incomeForms[type]?.component"
          v-if="incomeForms[type]?.component"
          v-model="formData[type]"
        />

        <!-- Fallback to basic amount field if component not yet created -->
        <div v-else>
          <Label :for="`${type}-amount`">Amount</Label>
          <Input
            :id="`${type}-amount`"
            v-model="formData[type].amount"
            type="text"
            placeholder="Enter amount"
            class="mt-1"
          />
        </div>
      </div>

      <div class="mt-8 flex justify-end">
        <Button @click="submitForm" type="submit" class="w-full md:w-auto">
          Submit Income Declaration
        </Button>
      </div>
    </div>
  </MainLayout>
</template>
